import {
  type AnchorHTMLAttributes, type ButtonHTMLAttributes, forwardRef, type ReactElement,type ReactNode, type Ref
} from 'react';
import { FiLoader } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { Icon, type IconProps } from '@/components/icon/icon';

import styles from './button.module.scss';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  icon?: ReactElement<IconProps>;
  asLink?: boolean;
  href?: string;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      icon,
      asLink = false,
      href,
      children,
      className,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const buttonClasses = [
      styles.button,
      styles[`button--${ variant }`],
      styles[`button--${ size }`],
      isDisabled && styles['button--disabled'],
      loading && styles['button--loading'],
      className
    ]
      .filter( Boolean )
      .join( ' ' );

    const content = (
      <>
        {loading ? (
          <Icon icon={ FiLoader } className={ styles.spinner } />
        ) : icon ? (
          <span className={ styles.icon }>{icon}</span>
        ) : null}
        {children && <span className={ styles.label }>{children}</span>}
      </>
    );

    if ( asLink && href ) {
      return (
        <Link
          to={ href }
          className={ buttonClasses }
          aria-disabled={ isDisabled }
          ref={ ref as Ref<HTMLAnchorElement> }
          { ...( props as AnchorHTMLAttributes<HTMLAnchorElement> ) }
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ ref as Ref<HTMLButtonElement> }
        type={ type }
        className={ buttonClasses }
        disabled={ isDisabled }
        aria-disabled={ isDisabled }
        { ...( props as ButtonHTMLAttributes<HTMLButtonElement> ) }
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';
